import {
  authenticatedFetch,
  extractApiErrorMessage,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import {
  PaginatedPreferenceCardsResponseDtoSchema,
  PreferenceCardResponseDto,
  PreferenceCardResponseDtoSchema,
  type CreatePreferenceCardDto,
  type PaginatedPreferenceCardsResponseDto,
  type PreferenceCardsFilterParams,
  type UpdatePreferenceCardDto,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

export class PreferenceCardsApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getPreferenceCards(
    options: PreferenceCardsFilterParams = {}
  ): Promise<PaginatedPreferenceCardsResponseDto> {
    const queryParams = new URLSearchParams();

    if (options.search) {
      queryParams.append('search', options.search);
    }

    const page = options.page !== undefined ? options.page + 1 : 1;
    const limit = options.limit ?? 10;
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    if (options.sortBy) queryParams.append('sortBy', options.sortBy);
    if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder);
    if (options.isActive !== undefined) queryParams.append('isActive', String(options.isActive));

    const url = `${this.resolveBaseUrl()}/preference-cards?${queryParams.toString()}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return PaginatedPreferenceCardsResponseDtoSchema.parse(await response.json());
  }

  async createPreferenceCard(
    dto: CreatePreferenceCardDto
  ): Promise<PreferenceCardResponseDto> {
    const url = `${this.resolveBaseUrl()}/preference-cards`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return PreferenceCardResponseDtoSchema.parse(await response.json());
  }

  async createPreferenceCardVersion(
    id: string,
    dto: UpdatePreferenceCardDto
  ): Promise<PreferenceCardResponseDto> {
    const url = `${this.resolveBaseUrl()}/preference-cards/${id}/versions`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return PreferenceCardResponseDtoSchema.parse(await response.json());
  }
}

export const createPreferenceCardsApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): PreferenceCardsApiClient => new PreferenceCardsApiClient(resolveBaseUrl);

export const preferenceCardsApiClient = new PreferenceCardsApiClient();

export type { BaseUrlResolver };
